import Box from "@mui/material/Box";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { invalidateEntireModuleListQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { useSelectedGamesData } from "entities/modules/stores/useSelectedGamesData";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { ContentBlocksModuleLayout, ModuleType } from "entities/modules/types";
import {
  validateContentBlocks,
  validateTopPrivacyTipsBlock,
} from "entities/modules/utils";
import { useCreatePageModuleMutation } from "entities/pages/mutations/useCreatePageModuleMutation";
import { useUpdatePageModuleMutation } from "entities/pages/mutations/useUpdatePageModuleMutation";
import {
  PageModuleDataToCreate,
  PageModuleDataToUpdate,
  PageModuleStatus,
} from "entities/pages/types";
import React, { FC, useMemo } from "react";
import { getTagsMatchRules } from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useModulePageDetails } from "../../context";
import { ManagedTranslations } from "../../context/types";
import { useGeneralData } from "../../stores/useGeneralData";
import { useStudioOrderingData } from "../../stores/useStudioOrderingData";
import { useStudiosData } from "../../stores/useStudiosData";
import { StyledPrimaryButton, StyledSecondaryButton } from "./styled";

const ActionButtons: FC = () => {
  const { moduleId, handleDrawerClose, brandId, pageId, moduleData } =
    useModulePageDetails();

  const {
    moduleName,
    moduleType,
    moduleLayout,
    adminNotes,
    translations,
    studioFilterEnabled,
    baseUrl,
    isInvalidBaseUrl,
    contentBlocks,
    threeCardBlockImages,
    oneCardBlockImage,
    bonusObtainType,
    rows,
    iconShown,
    icon,
    topPrivacyTipsBlock,
    topPrivacyTipsBlockImage,
    scheduledStartAt,
    scheduledEndAt,
    isSchedulingEnabled,
  } = useGeneralData(
    useShallow((state) => ({
      moduleName: state.moduleName,
      moduleType: state.moduleType,
      moduleLayout: state.moduleLayout,
      adminNotes: state.adminNotes,
      translations: state.translations,
      studioFilterEnabled: state.studioFilterEnabled,
      baseUrl: state.baseUrl,
      rows: state.rows,
      isInvalidBaseUrl: state.isInvalidBaseUrl,
      contentBlocks: state.contentBlocks,
      topPrivacyTipsBlock: state.topPrivacyTipsBlock,
      topPrivacyTipsBlockImage: state.topPrivacyTipsBlockImage,
      threeCardBlockImages: state.threeCardBlockImages,
      oneCardBlockImage: state.oneCardBlockImage,
      bonusObtainType: state.bonusObtainType,
      iconShown: state.iconShown,
      icon: state.icon,
      scheduledStartAt: state.scheduledStartAt,
      scheduledEndAt: state.scheduledEndAt,
      isSchedulingEnabled: state.isSchedulingEnabled,
    })),
  );

  const { selectedGames, orderType, manualSelectedGames, tagSelectedGames } =
    useSelectedGamesData(
      useShallow((state) => ({
        selectedGames: state.selectedGames,
        orderType: state.orderType,
        manualSelectedGames: state.manualSelectedGames,
        tagSelectedGames: state.tagSelectedGames,
      })),
    );

  const tagBlocks = useTagBlocksStore((state) => state.tagBlocks);
  const selectedStudios = useStudiosData((state) => state.selectedStudios);
  const orderedStudios = useStudioOrderingData((state) => state.orderedStudios);

  const areTagsIncorrect = useMemo(() => {
    const hasTagTypeWithoutTags = tagBlocks.some(
      (block) => !block.selectedTags.length,
    );
    return !tagBlocks.length || hasTagTypeWithoutTags;
  }, [tagBlocks]);

  const gamesTabsInvalid = useMemo(() => {
    if (moduleType !== ModuleType.Games) return false;
    if (!moduleLayout) return true;

    return !(manualSelectedGames?.length > 0 || !areTagsIncorrect);
  }, [moduleType, moduleLayout, manualSelectedGames, areTagsIncorrect]);
  const studiosTabsInvalid =
    moduleType === ModuleType.Studios &&
    (!moduleLayout || !selectedStudios.length);
  const productIframeInvalid =
    moduleType === ModuleType.ProductIframe && (!baseUrl || isInvalidBaseUrl);

  const cardBlockImages =
    moduleLayout === ContentBlocksModuleLayout.ThreeCardBlock
      ? threeCardBlockImages
      : oneCardBlockImage;

  // If moduleType is null, validation should fail
  const isContentBlocksValid =
    moduleType !== null &&
    validateContentBlocks(
      moduleType,
      moduleLayout,
      cardBlockImages,
      contentBlocks,
      brandId,
    );

  const isTopPrivacyTipsBlockValid =
    moduleType !== null &&
    validateTopPrivacyTipsBlock(
      moduleType,
      topPrivacyTipsBlockImage,
      topPrivacyTipsBlock,
    );
  const promotionsInvalid =
    moduleType === ModuleType.Promotion && (!moduleLayout || !bonusObtainType);

  const iconNotSelected = iconShown && !icon;

  const baseValidationFailed =
    !moduleName ||
    !moduleType ||
    gamesTabsInvalid ||
    studiosTabsInvalid ||
    productIframeInvalid ||
    !isContentBlocksValid ||
    !isTopPrivacyTipsBlockValid ||
    promotionsInvalid ||
    iconNotSelected;

  const schedulingIncomplete =
    isSchedulingEnabled && (!scheduledStartAt || !scheduledEndAt);

  const isDraftButtonDisabled = baseValidationFailed || isSchedulingEnabled;

  const isPublishButtonDisabled = baseValidationFailed || schedulingIncomplete;

  const { mutateAsync: createModule, isPending: isCreatingModule } =
    useCreatePageModuleMutation();

  const { mutateAsync: updateModule, isPending: isUpdatingModule } =
    useUpdatePageModuleMutation();

  const { allowed: hasWriteAccess } = useRolePermission("brandsAndPages:write");
  const { allowed: hasWriteTranslationsAccess } = useRolePermission(
    "brandsAndPages:translations:write",
  );

  const baseUrlToSubmit =
    moduleType === ModuleType.ProductIframe ? baseUrl : "";

  const handleSave = async (status: PageModuleStatus) => {
    if (
      (status === PageModuleStatus.Draft && isDraftButtonDisabled) ||
      (status === PageModuleStatus.Active && isPublishButtonDisabled)
    ) {
      return;
    }

    const tagTypeIds = new Set<string>();
    const tagIds = new Set<string>();

    tagBlocks.forEach((block) => {
      if (block.primaryTagId) {
        tagTypeIds.add(block.primaryTagId);
      }

      block.selectedTags.forEach((tag) => {
        tagIds.add(tag.id);
      });
    });

    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.ModuleName]: moduleName,
      },
    };

    const selectedGamesIdsByStudio = selectedGames.reduce(
      (acc: Record<string, string[]>, game) =>
        Object.assign(acc, {
          [String(game.studioId)]: [
            ...(acc[String(game.studioId)] ?? []),
            String(game.id),
          ],
        }),
      {},
    );

    const dataToSet: PageModuleDataToCreate = {
      name: moduleName,
      type: moduleType as ModuleType,
      layout: moduleLayout,
      status: scheduledStartAt ? PageModuleStatus.Draft : status,
      rows,
      adminNotes: adminNotes || null,
      translations: translationsWithDefaultValues,
      tagTypes: Array.from(tagTypeIds),
      tags: Array.from(tagIds),
      gamesOrderType: orderType,
      manualSelectedGames:
        manualSelectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      tagSelectedGames:
        tagSelectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      selectedGames:
        selectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      selectedStudiosIds: selectedStudios?.map((studio) => studio.id) ?? [],
      selectedGamesIdsByStudio,
      order: moduleData?.order ?? 1,
      studioFilterEnabled,
      orderedGamesStudiosIds: orderedStudios.map((studio) =>
        studio.id.toString(),
      ),
      baseUrl: baseUrlToSubmit,
      contentBlocks:
        contentBlocks?.map((block) => ({
          ...block,
          url: block.ctaDestination === "game" ? "" : (block.url ?? ""),
        })) ?? [],
      topPrivacyTipsBlock: topPrivacyTipsBlock,
      bonusObtainType: bonusObtainType ?? null,
      icon,
      scheduledStartAt,
      scheduledEndAt,
      tagsMatchRules: getTagsMatchRules(tagBlocks),
    };
    const cardBlockImages =
      dataToSet.layout === ContentBlocksModuleLayout.ThreeCardBlock
        ? threeCardBlockImages
        : oneCardBlockImage;

    if (moduleId) {
      await updateModule({
        moduleId,
        moduleData: dataToSet,
        brandId,
        pageId,
        cardBlockImages,
        topPrivacyTipsBlockImage,
      });
    } else {
      await createModule({
        data: dataToSet,
        brandId,
        pageId,
        cardBlockImages,
        topPrivacyTipsBlockImage,
      });
    }

    handleDrawerClose();
    invalidateEntireModuleListQuery();
  };

  const handleSaveTranslations = async () => {
    if (!moduleId) {
      return;
    }

    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.ModuleName]: moduleName,
      },
    };

    const dataToSet: PageModuleDataToUpdate = {
      translations: translationsWithDefaultValues,
    };

    await updateModule({
      moduleId,
      moduleData: dataToSet,
      brandId,
      pageId,
      cardBlockImages: [],
      topPrivacyTipsBlockImage,
    });

    handleDrawerClose();
    invalidateEntireModuleListQuery();
  };

  const handleSaveDraft = () => handleSave(PageModuleStatus.Draft);

  const handleSaveActive = () => handleSave(PageModuleStatus.Active);

  if (!hasWriteAccess && !hasWriteTranslationsAccess) {
    return null;
  }

  if (!hasWriteAccess && hasWriteTranslationsAccess) {
    return (
      <Box gap="1rem" display="flex" flexDirection="row" alignItems="center">
        <StyledPrimaryButton
          title="Save"
          size="small"
          disabled={baseValidationFailed}
          onClick={handleSaveTranslations}
          loading={isCreatingModule || isUpdatingModule}
        />
      </Box>
    );
  }

  return (
    <Box gap="1rem" display="flex" flexDirection="row" alignItems="center">
      <StyledSecondaryButton
        title="Save as draft"
        size="small"
        color="secondary"
        variant="outlined"
        disabled={isDraftButtonDisabled}
        onClick={handleSaveDraft}
        loading={isCreatingModule || isUpdatingModule}
      />
      <StyledPrimaryButton
        title="Save and Publish"
        size="small"
        disabled={isPublishButtonDisabled}
        onClick={handleSaveActive}
        loading={isCreatingModule || isUpdatingModule}
      />
    </Box>
  );
};

export default ActionButtons;
